/**
 * The Compare surfaces settings remembered per project (spec §12.10): the
 * result selection, compare mode, grid, tolerance, deviation mode and
 * sampling, under `localStorage['compareSurfaces.<projectId>']`.
 *
 * A convenience only: every access is wrapped, so a private window, blocked
 * storage or a corrupt entry simply means "nothing remembered" and the dialog
 * opens on its defaults. Each field is validated on its own, so one stale
 * value (an old mode name, say) does not throw the rest away.
 */
import { z } from 'zod';

import { PC_METHODS } from '@/components/SurfacePicker';

import { COMPARE_KINDS, COMPARE_RESULTS, DEVIATION_MODES } from './availability';
import type { CompareFormValues } from './formSchema';

export const REMEMBERED_KEY_PREFIX = 'compareSurfaces.';

const fields = {
  results: z.array(z.enum(COMPARE_RESULTS)).min(1),
  kind: z.enum(COMPARE_KINDS),
  /** Cut & fill grid, metres. */
  grid: z.number().positive(),
  /** Deviation tolerance, metres. */
  tolerance: z.number().positive(),
  deviationMode: z.enum(DEVIATION_MODES),
  /** Point-cloud sampling, applied to both sides. */
  sampling: z.enum(PC_METHODS),
};

export type RememberedSettings = { [K in keyof typeof fields]?: z.infer<(typeof fields)[K]> };

const storage = (): Storage | undefined => {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
};

/** This project's remembered settings, or `undefined` when there are none
 *  (or they cannot be read). */
export function loadRemembered(projectId: string): RememberedSettings | undefined {
  let raw: unknown;
  try {
    const text = storage()?.getItem(`${REMEMBERED_KEY_PREFIX}${projectId}`);
    if (!text) return undefined;
    raw = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(fields)) {
    const parsed = schema.safeParse((raw as Record<string, unknown>)[key]);
    if (parsed.success) out[key] = parsed.data;
  }
  return Object.keys(out).length ? (out as RememberedSettings) : undefined;
}

/** Which deviation settings the USER set (react-hook-form dirty flags), as
 *  opposed to values the coupling forced for a point-cloud or IFC design. */
export interface RememberEdited {
  tolerance?: boolean;
  deviationMode?: boolean;
}

/**
 * Remember what this submit used. Never throws.
 *
 * Tolerance and deviation mode are only remembered when the user edited them:
 * a PC design's forced 5 cm / vertical, or an IFC design's forced nearest,
 * would otherwise become the next LandXML run's default. Unedited, the
 * previously remembered value (if any) is kept.
 */
export function saveRemembered(projectId: string, v: CompareFormValues, edited: RememberEdited = {}): void {
  const previous = loadRemembered(projectId);
  const settings: RememberedSettings = {
    results: v.results,
    kind: v.kind,
    grid: v.cutFill.resolution,
    tolerance: edited.tolerance ? v.deviation.volumeTolerance : previous?.tolerance,
    deviationMode: edited.deviationMode ? v.deviation.mode : previous?.deviationMode,
    // The as-built / earlier side is the one a user samples deliberately.
    sampling: v.inputs.bottomPcMethod,
  };
  try {
    storage()?.setItem(`${REMEMBERED_KEY_PREFIX}${projectId}`, JSON.stringify(settings));
  } catch {
    // Quota, private mode, blocked site data: nothing to remember this time.
  }
}
